import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Container, Typography, CircularProgress, Grid } from '@mui/material';
import { Author, Book } from '../types';
import BookCard from './BookCard';
import { useNavigate } from 'react-router-dom';
import { translations } from '../translations';

const AuthorDetails = () => {
  const { id } = useParams();
  const [author, setAuthor] = useState<Author | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(document.documentElement.dir);
  const navigate = useNavigate();
  const t = translations[direction === 'rtl' ? 'ku' : 'en'];

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'dir') {
          setDirection(document.documentElement.dir);
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchAuthorDetails = async () => {
      try {
        const response = await axios.get(`/api/authors?id=${id}`);
        setAuthor(response.data.author);
        setBooks(response.data.books);
      } catch (error) {
        console.error('Error fetching author details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuthorDetails();
  }, [id]);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  const handleBookClick = (bookId: number) => {
    navigate(`/books/${bookId}`);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ my: 4 }}>
       {direction === 'rtl' ? author?.name_ku : author?.name}
      </Typography>
      {/* Add author details and books grid here */}
      <Typography variant="body1">
        {direction === 'rtl' 
          ? (author?.biography_ku && author?.biography_ku.length > 0 
              ? author?.biography_ku 
              : t.noBiography)
          : (author?.biography && author?.biography.length > 0 
              ? author?.biography 
              : t.noBiography)
        }
      </Typography>
      <Typography variant="body1">
        {author?.date_of_birth ? `Date of Birth: ${author?.date_of_birth}` : t.noDateOfBirth}
      </Typography>
      <Grid container spacing={2}>
        {books.map((book) => (
          <Grid item key={book?.id} xs={12} sm={6} md={4} lg={3}>
          <BookCard
            book={book}
            isRtl={direction === 'rtl'}
            onBookClick={handleBookClick}
            onAuthorClick={() => {}}
            formatNumber={(num: number | string) => num.toString()}
          />
        </Grid>
        ))}
        {books.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body1">No books found</Typography>
          </Grid>
        )}
        {loading && (
          <Grid item xs={12}>
            <CircularProgress />
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default AuthorDetails; 